(() => {
  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const money = (amount, currency = 'GBP') => new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format((amount || 0) / 100);

  const initialise = (root) => {
    if (root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';

    const mainImage = root.querySelector('[data-gallery-main]');
    const galleryLabel = root.querySelector('[data-gallery-label]');
    const galleryStage = root.querySelector('[data-gallery-stage]');
    root.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!mainImage) return;
        mainImage.src = button.dataset.src || '';
        mainImage.alt = button.dataset.alt || '';
        if (galleryLabel) galleryLabel.textContent = button.dataset.label || '';
        if (galleryStage && button.dataset.kind) galleryStage.className = `gallery-stage ${button.dataset.kind}`;
        root.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
          const active = thumb === button;
          thumb.classList.toggle('active', active);
          thumb.setAttribute('aria-pressed', String(active));
        });
      });
    });

    root.querySelectorAll('[data-accordion]').forEach((button) => {
      button.addEventListener('click', () => {
        const body = button.nextElementSibling;
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        const mark = button.lastElementChild;
        if (mark) mark.textContent = open ? '+' : '−';
        if (body instanceof HTMLElement) body.hidden = open;
      });
    });

    const technique = root.querySelector('[data-technique-overlay]');
    const techniqueOpen = root.querySelector('[data-technique-open]');
    const techniqueClose = root.querySelector('[data-technique-close]');
    const drawer = root.querySelector('[data-bag-drawer]');
    const scrim = root.querySelector('[data-bag-scrim]');
    const bagClose = root.querySelector('[data-bag-close]');
    const cartBody = root.querySelector('[data-cart-body]');
    let returnFocus = null;

    const lockPage = (locked) => { document.body.style.overflow = locked ? 'hidden' : ''; };
    const closeTechnique = () => {
      if (!technique) return;
      technique.hidden = true;
      technique.querySelector('video')?.pause();
      lockPage(false);
      returnFocus?.focus();
    };
    const openTechnique = () => {
      if (!technique) return;
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      technique.hidden = false;
      technique.querySelector('video')?.play().catch(() => undefined);
      lockPage(true);
      techniqueClose?.focus();
    };
    techniqueOpen?.addEventListener('click', openTechnique);
    techniqueClose?.addEventListener('click', closeTechnique);
    technique?.addEventListener('click', (event) => { if (event.target === technique) closeTechnique(); });

    const renderCart = (cart) => {
      root.querySelectorAll('[data-bag-count]').forEach((element) => { element.textContent = `(${cart.item_count})`; });
      root.querySelectorAll('[data-bag-open]').forEach((element) => element.setAttribute('aria-label', `Open bag with ${cart.item_count} items`));
      if (!cartBody) return;
      if (!cart.items.length) {
        cartBody.innerHTML = '<div class="empty-bag"><p>Your Bag Is Empty.</p><button type="button" data-continue-shopping>Continue Shopping</button></div>';
        cartBody.querySelector('[data-continue-shopping]')?.addEventListener('click', closeBag);
        return;
      }

      const items = cart.items.map((item) => {
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="160" height="200">`
          : '';
        return `<div class="bag-product">${image}<div><p class="bag-product-name">${escapeHtml(item.product_title)}</p><p>Quantity ${item.quantity}</p><p>${money(item.final_line_price, cart.currency)}</p></div><button type="button" data-remove-key="${escapeHtml(item.key)}">Remove</button></div>`;
      }).join('');
      const note = root.dataset.productKind === 'lash'
        ? 'Pre-Order · Dispatch Timing Confirmed By Email'
        : 'Complimentary Tracked UK Delivery';
      cartBody.innerHTML = `${items}<div class="drawer-total"><div><span>Subtotal</span><span>${money(cart.total_price, cart.currency)}</span></div><p>${note}</p><a href="${rootPath()}checkout">Secure Checkout</a></div>`;
      cartBody.querySelectorAll('[data-remove-key]').forEach((button) => {
        button.addEventListener('click', async () => {
          button.disabled = true;
          const response = await fetch(`${rootPath()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: button.dataset.removeKey, quantity: 0 }),
          });
          if (response.ok) renderCart(await response.json());
          else button.disabled = false;
        });
      });
    };

    const refreshCart = async () => {
      const response = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart');
      const cart = await response.json();
      renderCart(cart);
      return cart;
    };

    function closeBag() {
      if (!drawer || !scrim) return;
      drawer.classList.remove('open');
      scrim.classList.remove('visible');
      drawer.hidden = true;
      scrim.hidden = true;
      lockPage(false);
      returnFocus?.focus();
    }
    const openBag = async () => {
      if (!drawer || !scrim) return;
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      drawer.hidden = false;
      scrim.hidden = false;
      requestAnimationFrame(() => {
        drawer.classList.add('open');
        scrim.classList.add('visible');
      });
      lockPage(true);
      bagClose?.focus();
      try { await refreshCart(); } catch (_) { /* The drawer retains its safe loading state. */ }
    };
    root.querySelectorAll('[data-bag-open]').forEach((button) => button.addEventListener('click', openBag));
    bagClose?.addEventListener('click', closeBag);
    scrim?.addEventListener('click', closeBag);

    const form = root.querySelector('[data-product-form]');
    const addButton = form?.querySelector('[data-add-button]');
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!(form instanceof HTMLFormElement) || addButton?.disabled) return;
      const originalLabel = addButton?.textContent || '';
      if (addButton) { addButton.disabled = true; addButton.textContent = 'Adding…'; }
      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'This item could not be added.');
        }
        closeTechnique();
        await openBag();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : 'This item could not be added.');
      } finally {
        if (addButton) { addButton.disabled = false; addButton.textContent = originalLabel; }
      }
    });
    root.querySelectorAll('[data-add-trigger]').forEach((button) => button.addEventListener('click', () => {
      if (!button.disabled) form?.requestSubmit();
    }));

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (technique && !technique.hidden) closeTechnique();
      else if (drawer && !drawer.hidden) closeBag();
    });
  };

  const boot = () => document.querySelectorAll('[data-es-pdp]').forEach(initialise);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
